import React, { useState, useEffect, useContext } from "react";
import { Popconfirm, Table, Modal, Select } from "antd"
import {
  createDeductionType,
  editDeductioType,
  fetchDeductioTypeDetails,
  deleteDeductioType,
} from "../../service/deductionTypeService"
import { Link } from "react-router-dom"
import { PlusCircle } from "lucide-react"
import { UserContext } from "../../context/userContext";
import { hasPermission } from "../../core/json/permissions"; 

const DeductionType = () => {
  const { user } = useContext(UserContext);
  const [type, setType] = useState("")
  const [description, setDescription] = useState("")
  const [status, setStatus] = useState(true)
  const [edit, isEdit] = useState(false)
  const [dataSource, setDataSource] = useState()
  const [record, setRecord] = useState({})
  const [loading, setLoading] = useState(false)
  const [loading2, setLoading2] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })

  const fetchData = async (search = "") => {
    setLoading2(true)
    const data = await fetchDeductioTypeDetails({ search })
    setDataSource(data.data.deduction)
    setLoading2(false)
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchData(searchText)
    }, 400)
    return () => {
      clearTimeout(handler)
    }
  }, [searchText])

  const openModal = () => {
    setIsModalVisible(true)
  }

  const closeModal = () => {
    setIsModalVisible(false)
    handleReset()
  }

  const editData = async (record) => {
    setType(record.deducitionType)
    setDescription(record.description || "")
    setStatus(record.is_active)
    isEdit(true)
    setRecord(record)
    openModal()
  }

  const deleteData = async (record) => {
    await deleteDeductioType({ id: record._id })
    fetchData(searchText)
  }

  const columns = [
    {
      title: "Deduction Type",
      dataIndex: "deducitionType",
      key: "deducitionType",
      sorter: (a, b) => a.deducitionType.localeCompare(b.deducitionType),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (_, record) => (
        <div>{record.is_active ? "Active" : "Inactive"}</div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <td className="action-table-data">
          {/* Edit Button */}
          <Link
            className={`me-2 p-2 ${
              user?.permissions && hasPermission(user.permissions, "HRM", "Deduction Types", "edit") 
                ? '' 
                : 'disabled'
            }`}
            to="#"
            onClick={() => {
              if (user?.permissions && hasPermission(user.permissions, "HRM", "Deduction Types", "edit")) {
                editData(record);
              }
            }}
            title={
              !(user?.permissions && hasPermission(user.permissions, "HRM", "Deduction Types", "edit"))
                ? "You don't have permission to edit" 
                : "Edit"
            }
          >
            <i data-feather="download" className="feather-edit"></i>
          </Link>
    
          {/* Delete Button */}
          <Popconfirm
            title="Delete the Deduction-Type"
            description="Are you sure to delete this Deduction-Type?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => {
              if (user?.permissions && hasPermission(user.permissions, "HRM", "Deduction Types", "delete")) {
                deleteData(record);
              }
            }}
            disabled={
              !(user?.permissions && hasPermission(user.permissions, "HRM", "Deduction Types", "delete"))
            }
          >
            <Link 
              className={`confirm-text p-2 ${
                user?.permissions && hasPermission(user.permissions, "HRM", "Deduction Types", "delete") 
                  ? '' 
                  : 'disabled'
              }`}
              to="#"
              title={
                !(user?.permissions && hasPermission(user.permissions, "HRM", "Deduction Types", "delete"))
                  ? "You don't have permission to delete" 
                  : "Delete"
              }
            >
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </Popconfirm>
        </td>
      ),
    }
  ]

  const saveDeductionType = async () => {
    if (type === "") {
      alert("Please enter deduction type name")
      return
    }

    setLoading(true)
    try {
      if (edit) {
        await editDeductioType(record._id, { 
          deducitionType: type,
          description: description,
          is_active: status
        })
      } else {
        await createDeductionType({ 
          deducitionType: type,
          description: description,
          is_active: status
        })
      }
      closeModal()
      fetchData(searchText)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setType("")
    setDescription("")
    setStatus(true)
    isEdit(false)
    setRecord({})
  }

  const handleTableChange = (pagination) => {
    setPagination(pagination)
  }

  return (
    <div>
      <div className="page-wrapper-lt">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Deduction Types</h4>
                <h6>Manage Deduction Types</h6>
              </div>
              {user?.permissions && hasPermission(user.permissions, "HRM", "Deduction Types", "create") && (
                <button
                  className="btn btn-added ms-3"
                  onClick={openModal}
                  style={{ minWidth: '200px', minHeight: '40px' }}
                >
                  <PlusCircle className="me-2" size={20} />
                  Add Deduction Type
                </button>
              )}
            </div>
          </div>

          <div className="card table-list-card-grid">
            <div className="card-body">
              <div className="table-responsive">
                <div className="table-top">
                  <div className="search-set">
                    <div className="search-input">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search deduction types..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <Table
                  loading={loading2}
                  columns={columns}
                  rowKey="_id"
                  dataSource={dataSource}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    pageSizeOptions: ["5", "10", "20", "50"],
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Total ${total} items`
                  }}
                  onChange={handleTableChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={edit ? "Edit Deduction Type" : "Add Deduction Type"}
        open={isModalVisible}
        onCancel={closeModal}
        footer={null}
      >
        <div className="form-group mb-3">
          <label className="form-label">Deduction Type Name <span className="text-danger">*</span></label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter deduction type name"
            value={type}
            onChange={(e) => setType(e.target.value)}
          />
        </div>

        <div className="form-group mb-3">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows="3"
            placeholder="Enter description (e.g., Festival Advance - applied during festive period)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="form-group mb-3">
          <label className="form-label">Status</label>
          <div>
            <Select
              style={{ width: "100%" }}
              value={status}
              onChange={(value) => setStatus(value)}
            >
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </div>
        </div>

        <div className="modal-footer-btn" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <button className="btn btn-cancel" onClick={closeModal}>
            Cancel
          </button>
          <button 
            className="btn btn-submit" 
            onClick={saveDeductionType}
            disabled={loading}
          >
            {loading ? "Saving..." : edit ? "Update" : "Create"}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default DeductionType

